import { OfferFragment, StoredValueProgramsQuery } from '@occc/graphql';
import { earningMechanismHelper } from '@occc-app/offerHelpers/utils/earningMechanismHelper';
import { transformCustomData } from '@occc-app/routes/CampaignComponents/components/CustomData/utils';
import { getOfferNameAndDescriptionInitialValues } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionInitialValues';
import { getAwardCountInitialValues, LimitTimePeriod } from '@occc/feat-limits';
import {
  getDefaultTimezone,
  transformOfferToFulfillmentWindowFormValue,
} from '@occc/feat-constraints';

import { StoredValueConfigureFormInitialValues } from './stored-value-configure-form.types';

type StoredValueProgramNode =
  StoredValueProgramsQuery['storedValuePrograms']['edges'][number]['node'];

const getDefaultConfigureFormInitialValues = (): StoredValueConfigureFormInitialValues => ({
  storedValueDiscount: {},
  earningMechanism: {},
  awardCount: {
    isEnabled: false,
    global: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
    membership: {
      isEnabled: false,
      timePeriod: LimitTimePeriod.Total,
    },
  },
  fulfillmentWindow: {
    isEnabled: false,
    timezone: getDefaultTimezone(),
  },
});

const transformOfferEarningMechanism = (earningMechanism: OfferFragment['earningMechanism']) => {
  if (!earningMechanism) {
    return {};
  }

  if (earningMechanismHelper.isEarningMechanismStoredValueDiscount(earningMechanism)) {
    return {
      storedValueDiscount: {
        maxDiscountPerTransaction: earningMechanism.maxDiscountPerTransaction ?? undefined,
        excludedProductGroups: earningMechanism.excludedProductGroups ?? [],
      },
    };
  }

  return {};
};

const getNameAndDescriptionInitialValues = (
  offer?: OfferFragment,
  storedValueProgram?: StoredValueProgramNode | null,
) => {
  const fromOffer = getOfferNameAndDescriptionInitialValues(offer);

  if (storedValueProgram) {
    return {
      name: storedValueProgram.programName || fromOffer.name,
      description: fromOffer.description,
    };
  }

  return fromOffer;
};

const getReceiptContentInitialValues = (offer?: OfferFragment) => {
  const receiptContent = offer?.marketingContents?.[0]?.receiptContent;
  if (!receiptContent) {
    return undefined;
  }

  return {
    shortDescription: receiptContent.shortDescription || undefined,
    longDescription: receiptContent.longDescription || undefined,
  };
};

const getStoredValueProgramDiscountInitialValues = (
  storedValueProgram?: StoredValueProgramNode | null,
) => {
  if (!storedValueProgram) {
    return {};
  }

  return {
    id: storedValueProgram.id,
    pointsPerConversion: storedValueProgram.pointsPerConversion,
    moneyPerConversion: storedValueProgram.moneyPerConversion,
    minPointsPerConversion: storedValueProgram.minPointsPerConversion ?? undefined,
    maxPointsPerConversion: storedValueProgram.maxPointsPerConversion ?? undefined,
    expirationDays: storedValueProgram.expirationDays ?? undefined,
  };
};

export const transformStoredValueOfferToInitialValues = (
  offer?: OfferFragment,
  chainId?: string,
  storedValueProgram?: StoredValueProgramNode | null,
): StoredValueConfigureFormInitialValues => {
  const defaultValues = getDefaultConfigureFormInitialValues();

  if (!offer) {
    return {
      ...defaultValues,
      chainId,
      storedValueDiscount: {
        ...defaultValues.storedValueDiscount,
        ...getStoredValueProgramDiscountInitialValues(storedValueProgram),
      },
      ...getNameAndDescriptionInitialValues(undefined, storedValueProgram),
    };
  }

  const earningMechanismValues = transformOfferEarningMechanism(offer.earningMechanism);
  const earningMechanismStoredValueDiscount =
    'storedValueDiscount' in earningMechanismValues && earningMechanismValues.storedValueDiscount
      ? earningMechanismValues.storedValueDiscount
      : {};

  return {
    ...defaultValues,
    chainId,
    ...getNameAndDescriptionInitialValues(offer, storedValueProgram),
    earningMechanism: {
      id: offer.earningMechanism?.id,
    },
    storedValueDiscount: {
      ...defaultValues.storedValueDiscount,
      ...getStoredValueProgramDiscountInitialValues(storedValueProgram),
      ...earningMechanismStoredValueDiscount,
    },
    awardCount: getAwardCountInitialValues(offer, defaultValues.awardCount),
    ...transformOfferToFulfillmentWindowFormValue(offer),
    customData: transformCustomData(offer.customData),
    receiptContent: getReceiptContentInitialValues(offer),
  };
};
